import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AddProductModal = ({ product, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    category: 'HVAC',
    description: '',
    price: '',
    cost: '',
    quantity: '',
    reorderLevel: '',
    supplier: '',
    status: 'In Stock',
    unit: 'EA',
    customUnit: '',
    isTrackable: true
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (product) {
      const isCustomUnit = !['EA', 'HR', 'Day', 'Month']?.includes(product?.unit);
      setFormData({
        name: product?.name || '',
        sku: product?.sku || '',
        category: product?.category || 'HVAC',
        description: product?.description || '',
        price: product?.price?.toString() || '',
        cost: product?.cost?.toString() || '',
        quantity: product?.quantity?.toString() || '',
        reorderLevel: product?.reorderLevel?.toString() || '',
        supplier: product?.supplier || '',
        status: product?.status || 'In Stock',
        unit: isCustomUnit ? 'Custom' : (product?.unit || 'EA'),
        customUnit: isCustomUnit ? product?.unit : '',
        isTrackable: product?.isTrackable !== undefined ? product?.isTrackable : true
      });
      setTouched({});
      setErrors({});
    }
  }, [product]);

  const categoryOptions = [
    { value: 'HVAC', label: 'HVAC' },
    { value: 'Plumbing', label: 'Plumbing' },
    { value: 'Electrical', label: 'Electrical' },
    { value: 'Refrigeration', label: 'Refrigeration' },
    { value: 'Tools', label: 'Tools' },
    { value: 'Safety Equipment', label: 'Safety Equipment' }
  ];

  const statusOptions = [
    { value: 'In Stock', label: 'In Stock' },
    { value: 'Low Stock', label: 'Low Stock' },
    { value: 'Out of Stock', label: 'Out of Stock' }
  ];

  const unitOptions = [
    { value: 'EA', label: 'EA (Each)' },
    { value: 'HR', label: 'HR (Hour)' },
    { value: 'Day', label: 'Day' },
    { value: 'Month', label: 'Month' },
    { value: 'Custom', label: 'Custom Unit' }
  ];

  const validateField = (field, value) => {
    let error = '';

    switch (field) {
      case 'name':
        if (!value?.trim()) {
          error = 'Product name is required';
        } else if (value?.trim()?.length < 3) {
          error = 'Product name must be at least 3 characters';
        }
        break;
      case 'sku':
        if (!value?.trim()) {
          error = 'SKU is required';
        } else if (!/^[A-Z0-9-]+$/?.test(value)) {
          error = 'SKU must contain only uppercase letters, numbers, and hyphens';
        }
        break;
      case 'description':
        if (!value?.trim()) {
          error = 'Description is required';
        } else if (value?.trim()?.length < 10) {
          error = `Description must be at least 10 characters (${value?.trim()?.length}/10)`;
        }
        break;
      case 'price':
        if (!value || parseFloat(value) <= 0) {
          error = 'Valid price is required (must be greater than 0)';
        }
        break;
      case 'cost':
        if (!value || parseFloat(value) <= 0) {
          error = 'Valid cost is required (must be greater than 0)';
        } else if (parseFloat(value) > parseFloat(formData?.price || 0)) {
          error = 'Cost should not exceed price';
        }
        break;
      case 'quantity':
        if (formData?.isTrackable) {
          if (!value || parseInt(value) < 0) {
            error = 'Valid quantity is required for trackable products';
          }
        }
        break;
      case 'reorderLevel':
        if (formData?.isTrackable) {
          if (!value || parseInt(value) < 0) {
            error = 'Valid reorder level is required for trackable products';
          }
        }
        break;
      case 'supplier':
        if (!value?.trim()) {
          error = 'Supplier is required';
        }
        break;
      case 'customUnit':
        if (formData?.unit === 'Custom' && !value?.trim()) {
          error = 'Custom unit type is required';
        }
        break;
      default:
        break;
    }

    return error;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Real-time validation on change if field was touched
    if (touched?.[field]) {
      let error = validateField(field, value);
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    let error = validateField(field, formData?.[field]);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const validateForm = () => {
    const newErrors = {};
    const fieldsToValidate = [
      'name',
      'sku',
      'description',
      'price',
      'cost',
      'quantity',
      'reorderLevel',
      'supplier',
      'customUnit'
    ];

    fieldsToValidate?.forEach(field => {
      let error = validateField(field, formData?.[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();

    if (validateForm()) {
      const finalUnit = formData?.unit === 'Custom' ? formData?.customUnit : formData?.unit;
      
      const productData = {
        ...formData,
        price: parseFloat(formData?.price),
        cost: parseFloat(formData?.cost),
        quantity: formData?.isTrackable ? parseInt(formData?.quantity) : 0,
        reorderLevel: formData?.isTrackable ? parseInt(formData?.reorderLevel) : 0,
        unit: finalUnit,
        isTrackable: formData?.isTrackable
      };

      delete productData?.customUnit;

      onSave(productData);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[1400] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-5 w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="Package" size={20} color="var(--color-primary)" />
            </div>
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
              {product ? 'Edit Product' : 'Add New Product'}
            </h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={24} color="var(--color-foreground)" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Product Name"
              type="text"
              placeholder="Enter product name"
              value={formData?.name}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              onBlur={() => handleBlur('name')}
              error={touched?.name ? errors?.name : ''}
              required
            />

            <Input
              label="SKU"
              type="text"
              placeholder="e.g., HVAC-FLT-001"
              value={formData?.sku}
              onChange={(e) => handleInputChange('sku', e?.target?.value?.toUpperCase())}
              onBlur={() => handleBlur('sku')}
              error={touched?.sku ? errors?.sku : ''}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Category"
              required
              options={categoryOptions}
              value={formData?.category}
              onChange={(value) => handleInputChange('category', value)}
            />

            <Select
              label="Status"
              required
              options={statusOptions}
              value={formData?.status}
              onChange={(value) => handleInputChange('status', value)}
            />
          </div>

          <Input
            label="Description"
            type="text"
            placeholder="Product description"
            value={formData?.description}
            onChange={(e) => handleInputChange('description', e?.target?.value)}
            onBlur={() => handleBlur('description')}
            error={touched?.description ? errors?.description : ''}
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Price"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={formData?.price}
              onChange={(e) => handleInputChange('price', e?.target?.value)}
              onBlur={() => handleBlur('price')}
              error={touched?.price ? errors?.price : ''}
              required
            />

            <Input
              label="Cost"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={formData?.cost}
              onChange={(e) => handleInputChange('cost', e?.target?.value)}
              onBlur={() => handleBlur('cost')}
              error={touched?.cost ? errors?.cost : ''}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Unit Type"
              required
              options={unitOptions}
              value={formData?.unit}
              onChange={(value) => handleInputChange('unit', value)}
            />

            {formData?.unit === 'Custom' && (
              <Input
                label="Custom Unit Type"
                type="text"
                placeholder="e.g., Box, Pallet, Roll"
                value={formData?.customUnit}
                onChange={(e) => handleInputChange('customUnit', e?.target?.value)}
                onBlur={() => handleBlur('customUnit')}
                error={touched?.customUnit ? errors?.customUnit : ''}
                required
              />
            )}
          </div>

          <div className="bg-muted/30 rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-foreground">Inventory Tracking</label>
                <p className="text-xs text-muted-foreground mt-1">
                  Enable for products that require inventory management
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleInputChange('isTrackable', !formData?.isTrackable)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData?.isTrackable ? 'bg-primary' : 'bg-muted'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData?.isTrackable ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {formData?.isTrackable && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-border">
                <Input
                  label="Quantity"
                  type="number"
                  placeholder="0"
                  value={formData?.quantity}
                  onChange={(e) => handleInputChange('quantity', e?.target?.value)}
                  onBlur={() => handleBlur('quantity')}
                  error={touched?.quantity ? errors?.quantity : ''}
                  required={formData?.isTrackable}
                  disabled={!formData?.isTrackable}
                />

                <Input
                  label="Reorder Level"
                  type="number"
                  placeholder="0"
                  value={formData?.reorderLevel}
                  onChange={(e) => handleInputChange('reorderLevel', e?.target?.value)}
                  onBlur={() => handleBlur('reorderLevel')}
                  error={touched?.reorderLevel ? errors?.reorderLevel : ''}
                  required={formData?.isTrackable}
                  disabled={!formData?.isTrackable}
                />
              </div>
            )}
          </div>

          <Input
            label="Supplier"
            type="text"
            placeholder="Supplier name"
            value={formData?.supplier}
            onChange={(e) => handleInputChange('supplier', e?.target?.value)}
            onBlur={() => handleBlur('supplier')}
            error={touched?.supplier ? errors?.supplier : ''}
            required
          />
        </form>

        <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="default" onClick={handleSubmit}>
            {product ? 'Update Product' : 'Add Product'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddProductModal;